import {
  ArrowUpCircle,
  Download,
  type LucideIcon,
  Play,
  RefreshCw,
  RotateCw,
  ScrollText,
  Square,
  Undo2,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AdminAlert } from "../components/AdminAlert";
import { AppForm } from "../components/AppForm";
import { DeleteAppDialog } from "../components/DeleteAppDialog";
import { InfoTooltip } from "../components/InfoTooltip";
import { RuntimeStatusBadge } from "../components/RuntimeStatusBadge";
import { ShareAppPanel } from "../components/ShareAppPanel";
import { useApi } from "../hooks/useApi";
import { serializeRules } from "../lib/access-rules";
import {
  ApiError,
  type AppDeployment,
  type AppMetadataFull,
  type AppRuntime,
  type EnvTemplateEntry,
  type EnvTemplateResult,
  type RuntimeConfigInput,
  type RuntimeStatusResponse,
  type SalesforceOrg,
  type UpdateCheckResult,
  api,
} from "../lib/api";
import { applyPrefill, applyPrefillAll, configuredKeys } from "../lib/env-template-prefill";
import { type RuntimeActionKind, availableActions } from "../lib/runtime-actions";

export interface AdminAppDetailProps {
  appId: string;
  onBack: () => void;
  /** #6: optionaler Start-Tab (Teilen-Icon der Kachel springt direkt auf "share"). */
  initialTab?: DetailTab;
  /** #8: Sharing-UI (Teilen-Tab, shareable-Checkbox) nur im Showroom-Modus zeigen. */
  sharingEnabled?: boolean;
}

interface FormState {
  image_ref: string;
  internal_port: string;
  env_text: string;
  // Issue #69: neue/zu ersetzende Secret-WERTE als KEY=value-Zeilen. Die Werte
  // bestehender Secrets werden nie geladen (nur ihre Keys), daher ist dieses Feld
  // beim Laden immer leer.
  env_secrets_text: string;
  // Bereits gesetzte Secret-Keys (maskiert dargestellt) und die zum Löschen
  // markierten Keys. Löschen passiert ausschließlich explizit, nie durch das
  // Auslassen einer Zeile.
  existing_secret_keys: string[];
  secret_deletes: string[];
  volumes_text: string;
}

const EMPTY_FORM: FormState = {
  image_ref: "",
  // Leer = der Hub liest den Port beim Deploy aus dem Image (EXPOSE).
  internal_port: "",
  env_text: "",
  env_secrets_text: "",
  existing_secret_keys: [],
  secret_deletes: [],
  volumes_text: "",
};

function runtimeToForm(r: AppRuntime): FormState {
  const env = safeParseEnv(r.env_json);
  const volumes = safeParseVolumes(r.volumes_json);
  return {
    image_ref: r.image_ref,
    internal_port: r.internal_port == null ? "" : String(r.internal_port),
    env_text: Object.entries(env)
      .map(([k, v]) => `${k}=${v}`)
      .join("\n"),
    env_secrets_text: "",
    existing_secret_keys: [...(r.env_secret_keys ?? [])].sort(),
    secret_deletes: [],
    volumes_text: volumes.map((v) => `${v.name}=${v.container_path}`).join("\n"),
  };
}

function formToPayload(f: FormState): RuntimeConfigInput {
  const env: Record<string, string> = {};
  for (const line of f.env_text.split("\n")) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    const idx = trimmed.indexOf("=");
    if (idx <= 0) continue;
    const k = trimmed.slice(0, idx).trim();
    const v = trimmed.slice(idx + 1);
    if (k) env[k] = v;
  }
  const volumes: Array<{ name: string; container_path: string }> = [];
  for (const line of f.volumes_text.split("\n")) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    const idx = trimmed.indexOf("=");
    if (idx <= 0) continue;
    volumes.push({
      name: trimmed.slice(0, idx).trim(),
      container_path: trimmed.slice(idx + 1).trim(),
    });
  }
  // Issue #69: neue/zu ersetzende Secret-Werte. Zeilen ohne Wert (KEY= ) werden
  // verworfen — "behalten" heißt: Key nicht senden. Löschen geht nur über die
  // explizit markierten secret_deletes.
  const envSecrets: Record<string, string> = {};
  for (const line of f.env_secrets_text.split("\n")) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    const idx = trimmed.indexOf("=");
    if (idx <= 0) continue;
    const k = trimmed.slice(0, idx).trim();
    const v = trimmed.slice(idx + 1);
    if (k && v !== "") envSecrets[k] = v;
  }
  const secretDeletes = f.secret_deletes.filter((k) => f.existing_secret_keys.includes(k));
  const payload: RuntimeConfigInput = {
    image_ref: f.image_ref.trim(),
  };
  const portRaw = f.internal_port.trim();
  if (portRaw) {
    const port = Number.parseInt(portRaw, 10);
    if (Number.isFinite(port)) payload.internal_port = port;
  }
  if (Object.keys(env).length > 0) payload.env = env;
  if (Object.keys(envSecrets).length > 0) payload.env_secrets = envSecrets;
  if (secretDeletes.length > 0) payload.env_secret_deletes = secretDeletes;
  if (volumes.length > 0) payload.volumes = volumes;
  return payload;
}

function safeParseEnv(raw: string): Record<string, string> {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      const out: Record<string, string> = {};
      for (const [k, v] of Object.entries(parsed)) {
        if (typeof v === "string") out[k] = v;
      }
      return out;
    }
    return {};
  } catch {
    return {};
  }
}

function safeParseVolumes(raw: string): Array<{ name: string; container_path: string }> {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((v): v is { name: string; container_path: string } => {
        if (typeof v !== "object" || v === null) return false;
        const o = v as Record<string, unknown>;
        return typeof o.name === "string" && typeof o.container_path === "string";
      })
      .map((v) => ({ name: v.name, container_path: v.container_path }));
  } catch {
    return [];
  }
}

function shortDigest(digest: string | null): string {
  if (!digest) return "—";
  const m = digest.match(/^sha256:([a-f0-9]{12})/);
  return m ? `sha256:${m[1]}…` : digest;
}

function shortenId(id: string): string {
  if (id.length <= 9) return id;
  return `${id.slice(0, 8)}…`;
}

function formatDeployedAt(iso: string | null, lang: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat(lang, { dateStyle: "medium", timeStyle: "short" }).format(d);
}

type DetailTab = "status" | "config" | "runtime" | "share";

export function AdminAppDetail(props: AdminAppDetailProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const [refresh, setRefresh] = useState(0);
  const appInfo = useApi(() => api.admin.app(props.appId), [props.appId, refresh]);
  // Runtime/container concepts only apply to managed apps; the runtime endpoint
  // 422s for proxy/external. Once the app type is known, skip the call for
  // non-managed apps and resolve to an empty status instead.
  const isManaged = appInfo.status === "ready" && appInfo.data.app_type === "managed";
  const status = useApi<RuntimeStatusResponse>(
    () =>
      isManaged
        ? api.admin.runtime.get(props.appId)
        : Promise.resolve({ runtime: null, recent_deployments: [], metadata: null }),
    [props.appId, refresh, isManaged],
  );
  // SF orgs are needed for the config tab's Salesforce-binding editor. A 404/401
  // (SF disabled or not authorised) is tolerated as an empty list, matching AdminApps.
  const sfOrgs = useApi(async () => {
    try {
      const res = await api.admin.salesforce.orgs();
      return res.orgs;
    } catch (e) {
      if (e instanceof ApiError && (e.status === 404 || e.status === 401))
        return [] as SalesforceOrg[];
      throw e;
    }
  }, []);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [busy, setBusy] = useState<
    | null
    | "save"
    | "deploy"
    | "stop"
    | "start"
    | "restart"
    | "rollback"
    | "checkUpdate"
    | "pull"
    | "envTemplate"
  >(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [updateCheck, setUpdateCheck] = useState<UpdateCheckResult | null>(null);
  const [logs, setLogs] = useState<string>("");
  const [envTemplate, setEnvTemplate] = useState<EnvTemplateResult | null>(null);
  const [tab, setTab] = useState<DetailTab>(props.initialTab ?? "status");
  const [deleting, setDeleting] = useState(false);
  const logsRef = useRef<HTMLPreElement>(null);

  useEffect(() => {
    if (status.status === "ready" && status.data.runtime) {
      setForm(runtimeToForm(status.data.runtime));
    }
  }, [status]);

  // Latest logs live on the Status tab and load automatically for managed apps.
  // Failures stay silent (the log area just stays empty); the explicit "reload
  // logs" action surfaces errors when the operator asks for them.
  useEffect(() => {
    if (!isManaged || status.status !== "ready" || !status.data.runtime) return;
    let cancelled = false;
    api.admin.runtime
      .logs(props.appId, { tail: 200 })
      .then((r) => {
        if (!cancelled) setLogs(r.logs);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isManaged, status, props.appId]);

  // Logs keep docker's chronological order (oldest first); scroll to the bottom
  // so the newest lines are in view on load and after every reload, like `tail`.
  // biome-ignore lint/correctness/useExhaustiveDependencies: logs/tab sind bewusste Re-Trigger (neu scrollen bei Log-Update bzw. Tab-Wechsel), nicht im Body gelesene Werte.
  useEffect(() => {
    const el = logsRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [logs, tab]);

  const reload = (): void => {
    setRefresh((r) => r + 1);
  };

  const onSave = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setNotice(null);
    setBusy("save");
    try {
      await api.admin.runtime.configure(props.appId, formToPayload(form));
      setNotice(t("admin.runtime.savedHint"));
      reload();
    } catch (err) {
      setError(err instanceof ApiError ? (err.detail ?? err.message) : String(err));
    } finally {
      setBusy(null);
    }
  };

  const onDeploy = async (): Promise<void> => {
    setError(null);
    setNotice(null);
    setBusy("deploy");
    try {
      const dep = await api.admin.runtime.deploy(props.appId);
      if (dep.status !== "healthy") {
        setError(t("admin.runtime.deployFailedHint"));
      }
      setUpdateCheck(null);
      reload();
    } catch (err) {
      setError(err instanceof ApiError ? (err.detail ?? err.message) : String(err));
    } finally {
      setBusy(null);
    }
  };

  const onStop = async (): Promise<void> => {
    setError(null);
    setNotice(null);
    setBusy("stop");
    try {
      await api.admin.runtime.stop(props.appId);
      setUpdateCheck(null);
      reload();
    } catch (err) {
      setError(err instanceof ApiError ? (err.detail ?? err.message) : String(err));
    } finally {
      setBusy(null);
    }
  };

  const onStart = async (): Promise<void> => {
    setError(null);
    setNotice(null);
    setBusy("start");
    try {
      await api.admin.runtime.start(props.appId);
      reload();
    } catch (err) {
      setError(err instanceof ApiError ? (err.detail ?? err.message) : String(err));
    } finally {
      setBusy(null);
    }
  };

  const onRestart = async (): Promise<void> => {
    if (!window.confirm(t("admin.runtime.restartConfirm"))) return;
    setError(null);
    setNotice(null);
    setBusy("restart");
    try {
      await api.admin.runtime.restart(props.appId);
      reload();
    } catch (err) {
      setError(err instanceof ApiError ? (err.detail ?? err.message) : String(err));
    } finally {
      setBusy(null);
    }
  };

  const onRollback = async (): Promise<void> => {
    if (!window.confirm(t("admin.runtime.rollbackConfirm"))) return;
    setError(null);
    setNotice(null);
    setBusy("rollback");
    try {
      await api.admin.runtime.rollback(props.appId);
      setUpdateCheck(null);
      reload();
    } catch (err) {
      setError(err instanceof ApiError ? (err.detail ?? err.message) : String(err));
    } finally {
      setBusy(null);
    }
  };

  const onCheckUpdate = async (): Promise<void> => {
    setError(null);
    setNotice(null);
    setBusy("checkUpdate");
    try {
      const result = await api.admin.runtime.checkUpdate(props.appId);
      setUpdateCheck(result);
      if (result.image_pinned_to_digest) {
        setNotice(t("admin.runtime.updateDigestPinned"));
      } else if (result.update_available && result.current_digest === null) {
        setNotice(
          t("admin.runtime.updateAvailableFirstTime", {
            available: shortDigest(result.available_digest),
          }),
        );
      } else if (result.update_available) {
        setNotice(
          t("admin.runtime.updateAvailable", {
            deployedAt: formatDeployedAt(result.current_deployed_at, i18n.language),
            current: shortDigest(result.current_digest),
            available: shortDigest(result.available_digest),
          }),
        );
      } else {
        setNotice(
          t("admin.runtime.updateUpToDate", {
            deployedAt: formatDeployedAt(result.current_deployed_at, i18n.language),
          }),
        );
      }
      // Persistierter Stand hat sich geändert — neu laden, damit Badge und
      // Aktionsleiste den gespeicherten update_available-Wert widerspiegeln (#16).
      reload();
    } catch (err) {
      if (err instanceof ApiError && err.kind === "timeout") {
        setError(t("admin.runtime.checkUpdateErrorTimeout"));
      } else if (err instanceof ApiError && err.kind === "auth") {
        setError(t("admin.runtime.checkUpdateErrorAuth"));
      } else if (err instanceof ApiError && err.kind === "unknown") {
        setError(
          t("admin.runtime.checkUpdateErrorRegistry", { detail: err.detail ?? err.message }),
        );
      } else {
        setError(err instanceof ApiError ? (err.detail ?? err.message) : String(err));
      }
    } finally {
      setBusy(null);
    }
  };

  const onPull = async (): Promise<void> => {
    setError(null);
    setNotice(null);
    setBusy("pull");
    try {
      const result = await api.admin.runtime.pull(props.appId);
      setUpdateCheck(result);
      setNotice(
        result.update_available
          ? t("admin.runtime.pullDoneUpdate")
          : t("admin.runtime.pullDoneUpToDate"),
      );
      // Persistierter Stand (available_digest/update_available) kann sich geändert
      // haben — neu laden, ohne den laufenden Container anzufassen.
      reload();
    } catch (err) {
      if (err instanceof ApiError && err.kind === "timeout") {
        setError(t("admin.runtime.checkUpdateErrorTimeout"));
      } else if (err instanceof ApiError && err.kind === "auth") {
        setError(t("admin.runtime.checkUpdateErrorAuth"));
      } else {
        setError(err instanceof ApiError ? (err.detail ?? err.message) : String(err));
      }
    } finally {
      setBusy(null);
    }
  };

  const onFetchEnvTemplate = async (): Promise<void> => {
    setError(null);
    setNotice(null);
    setBusy("envTemplate");
    try {
      const result = await api.admin.runtime.envTemplate(props.appId);
      setEnvTemplate(result);
      if (result.source_path === null) {
        setNotice(t("admin.runtime.envTemplateEmpty"));
      }
    } catch (err) {
      setError(err instanceof ApiError ? (err.detail ?? err.message) : String(err));
    } finally {
      setBusy(null);
    }
  };

  // Issue #77: SECRET-Hints werden in das Feld „Secret-Werte" (env_secrets_text)
  // gescaffoldet, NICHT mehr ins Referenzen-Feld — Logik in
  // ../lib/env-template-prefill.ts (testbar). Bereits gesetzte Keys → No-op.
  const prefillEntry = (entry: EnvTemplateEntry): void => {
    setForm((prev) => applyPrefill(prev, entry));
  };

  const prefillAllMissing = (): void => {
    if (!envTemplate) return;
    setForm((prev) => applyPrefillAll(prev, envTemplate.entries));
  };

  const onRefreshLogs = async (): Promise<void> => {
    try {
      const r = await api.admin.runtime.logs(props.appId, { tail: 200 });
      setLogs(r.logs);
    } catch (err) {
      setError(err instanceof ApiError ? (err.detail ?? err.message) : String(err));
    }
  };

  // #116: Löschen ist auch auf der Detailseite verfügbar — derselbe Self-Service-
  // Pfad, den Owner über das Launchpad-Zahnrad erreichen (nicht nur die Admin-
  // Liste). Bei Erfolg zurück zur Herkunftsansicht (Liste bzw. Launchpad).
  const onDelete = async (keepVolumes: boolean): Promise<void> => {
    setError(null);
    try {
      await api.admin.delete(props.appId, keepVolumes);
      setDeleting(false);
      props.onBack();
    } catch (err) {
      setError(err instanceof ApiError ? (err.detail ?? err.message) : String(err));
    }
  };

  if (appInfo.status === "loading") return <p className="launchpad__hint">{t("admin.loading")}</p>;
  if (appInfo.status === "error") {
    return <p className="launchpad__hint launchpad__hint--error">{appInfo.error.message}</p>;
  }
  const app = appInfo.data;
  const sfOrgsForForm: SalesforceOrg[] = sfOrgs.status === "ready" ? sfOrgs.data : [];
  const runtime = status.status === "ready" ? status.data.runtime : null;

  // #6: Tab-Liste. Managed Apps haben Status/Config/Runtime; nicht-Managed nur
  // Config. Ein "Teilen"-Tab kommt hinzu, wenn die App `shareable` ist (Showroom).
  // `activeTab` faengt einen Tab-Wert ab, der (noch) nicht in der Liste ist —
  // z. B. Default "status" bei einer nicht-Managed App.
  const tabs: DetailTab[] = [
    ...(isManaged ? (["status", "config", "runtime"] as DetailTab[]) : (["config"] as DetailTab[])),
    ...(app.shareable && props.sharingEnabled ? (["share"] as DetailTab[]) : []),
  ];
  const activeTab: DetailTab = tabs.includes(tab) ? tab : (tabs[0] ?? "config");
  const showTabs = tabs.length > 1;

  // #9: Direkter Sprung von den Einstellungen zur laufenden App. Externe Apps
  // oeffnen in neuem Tab; interne (proxy/managed) navigieren auf ihren Pfad.
  const appIsExternal = app.app_type === "external";
  const appHref = appIsExternal ? app.target_url : app.public_path;
  const deployments = status.status === "ready" ? status.data.recent_deployments : [];
  const metadata = status.status === "ready" ? status.data.metadata : null;
  const hasPrevious =
    deployments[0]?.previous_digest !== null && deployments[0]?.previous_digest !== undefined;

  const actions = availableActions({
    runtime: runtime
      ? { observed_state: runtime.observed_state, pinned_digest: runtime.pinned_digest }
      : null,
    hasPrevious,
    // Gespeicherter Stand (#16, vom periodischen Job/Persist) als Basis; ein
    // frischer manueller Check überschreibt ihn für die aktuelle Ansicht.
    updateAvailable: updateCheck?.update_available ?? runtime?.update_available ?? false,
  });

  // kind → (Handler, vorhandener i18n-Key). deploy/redeploy/update lösen denselben Deploy-Endpoint aus.
  const ACTION_META: Record<
    RuntimeActionKind,
    { labelKey: string; icon: LucideIcon; run: () => void }
  > = {
    deploy: { labelKey: "admin.runtime.deployButton", icon: Play, run: () => void onDeploy() },
    redeploy: {
      labelKey: "admin.runtime.redeployButton",
      icon: RotateCw,
      run: () => void onDeploy(),
    },
    update: {
      labelKey: "admin.runtime.updateDeployButton",
      icon: ArrowUpCircle,
      run: () => void onDeploy(),
    },
    pull: { labelKey: "admin.runtime.pullButton", icon: Download, run: () => void onPull() },
    start: { labelKey: "admin.runtime.startButton", icon: Play, run: () => void onStart() },
    stop: { labelKey: "admin.runtime.stopButton", icon: Square, run: () => void onStop() },
    restart: {
      labelKey: "admin.runtime.restartButton",
      icon: RotateCw,
      run: () => void onRestart(),
    },
    rollback: {
      labelKey: "admin.runtime.rollbackButton",
      icon: Undo2,
      run: () => void onRollback(),
    },
    checkUpdate: {
      labelKey: "admin.runtime.checkUpdateButton",
      icon: RefreshCw,
      run: () => void onCheckUpdate(),
    },
    logs: {
      labelKey: "admin.runtime.logsRefresh",
      icon: ScrollText,
      run: () => void onRefreshLogs(),
    },
  };

  return (
    <section className="admin">
      <div className="admin__header">
        <button type="button" className="lang-toggle" onClick={props.onBack}>
          {t("admin.runtime.back")}
        </button>
        <h2>{t(isManaged ? "admin.runtime.title" : "admin.detail.title", { name: app.name })}</h2>
        {appHref ? (
          <a
            className="lang-toggle"
            href={appHref}
            {...(appIsExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})}
          >
            {t("admin.detail.openApp")}
          </a>
        ) : null}
        <button type="button" className="admin__danger" onClick={() => setDeleting(true)}>
          {t("admin.delete")}
        </button>
      </div>
      {showTabs && (
        <nav className="admin__tabs">
          {tabs.map((id) => (
            <button
              type="button"
              key={id}
              className={activeTab === id ? "active" : undefined}
              onClick={() => setTab(id)}
            >
              {t(`admin.detail.tab.${id}`)}
            </button>
          ))}
        </nav>
      )}
      {error ? <AdminAlert kind="error">{error}</AdminAlert> : null}
      {notice ? <AdminAlert kind="success">{notice}</AdminAlert> : null}

      {isManaged && activeTab === "status" && (
        <>
          <p className="admin__statusrow">
            <strong>{t("admin.runtime.col.status")}:</strong>{" "}
            <RuntimeStatusBadge state={runtime?.observed_state ?? "unknown"} />
          </p>
          {runtime?.update_available ? (
            <p className="admin__statusrow">
              <em className="badge badge--update">{t("admin.runtime.updateBadge")}</em>{" "}
              <small>
                {runtime.update_checked_at
                  ? t("admin.runtime.updateCheckedAt", {
                      at: formatDeployedAt(runtime.update_checked_at, i18n.language),
                    })
                  : t("admin.runtime.updateNeverChecked")}
              </small>
            </p>
          ) : null}

          {busy === "deploy" ||
          busy === "stop" ||
          busy === "start" ||
          busy === "restart" ||
          busy === "rollback" ||
          busy === "checkUpdate" ||
          busy === "pull" ? (
            <output className="admin__busy" aria-live="polite">
              <span className="spinner" aria-hidden="true" />
              {t(`admin.runtime.busy.${busy}`)}
            </output>
          ) : null}

          <div className="admin__actions">
            {(["container", "image", "diagnostics"] as const).map((group) => {
              const groupActions = actions.filter((a) => a.group === group);
              if (groupActions.length === 0) return null;
              return (
                <div className="admin__actions-group" key={group}>
                  {groupActions.map((a) => {
                    const Icon = ACTION_META[a.kind].icon;
                    return (
                      <button
                        type="button"
                        key={a.kind}
                        className={a.primary ? "primary" : undefined}
                        disabled={busy !== null}
                        title={t(ACTION_META[a.kind].labelKey)}
                        onClick={ACTION_META[a.kind].run}
                      >
                        <Icon size={15} aria-hidden="true" />
                        {t(ACTION_META[a.kind].labelKey)}
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>

          <h3>{t("admin.runtime.logsHeading")}</h3>
          {logs ? (
            <pre className="admin__logs" ref={logsRef}>
              {logs}
            </pre>
          ) : (
            <p className="launchpad__hint">{t("admin.runtime.logsEmpty")}</p>
          )}

          <h3>{t("admin.runtime.historyHeading")}</h3>
          <DeploymentHistory deployments={deployments} />
        </>
      )}

      {activeTab === "config" && (
        <AppForm
          sharingEnabled={props.sharingEnabled ?? false}
          initial={{
            slug: app.slug,
            name: app.name,
            description: app.description ?? "",
            app_type: app.app_type,
            upstream_url: app.upstream_url ?? "",
            target_url: app.target_url ?? "",
            strip_prefix: app.strip_prefix,
            category: app.category ?? "",
            visibility: app.visibility,
            shareable: app.shareable,
            access_rules: app.access_rules.map((r) => ({ type: r.type, value: r.value })),
            image_ref: "",
            internal_port: "",
            salesforce_required: app.proxy_settings.salesforce_required,
            salesforce_org_alias: app.proxy_settings.salesforce_org_alias,
          }}
          lockType={true}
          title={t("admin.detail.tab.config")}
          sfOrgs={sfOrgsForForm}
          onCancel={() => {
            if (isManaged) setTab("status");
            else props.onBack();
          }}
          onSubmit={async (form) => {
            const patch: Record<string, unknown> = {
              slug: form.slug,
              name: form.name,
              description: form.description || null,
              category: form.category || null,
              visibility: form.visibility,
              shareable: form.shareable && !form.salesforce_required,
              access_rules: serializeRules(form.access_rules),
            };
            if (form.app_type === "external") {
              patch.target_url = form.target_url;
            } else if (form.app_type === "proxy") {
              patch.upstream_url = form.upstream_url;
              patch.strip_prefix = form.strip_prefix;
            } else {
              patch.strip_prefix = form.strip_prefix;
            }
            if (form.app_type !== "external") {
              patch.proxy_settings = {
                salesforce_required: form.salesforce_required,
                salesforce_org_alias: form.salesforce_required ? form.salesforce_org_alias : null,
              };
            }
            setError(null);
            try {
              await api.admin.update(props.appId, patch);
              setNotice(t("admin.runtime.savedHint"));
              reload();
            } catch (err) {
              setError(err instanceof ApiError ? (err.detail ?? err.message) : String(err));
            }
          }}
          submitLabel={t("admin.save")}
        />
      )}

      {activeTab === "share" && <ShareAppPanel appId={props.appId} appName={app.name} />}

      {isManaged && activeTab === "runtime" && (
        <>
          <h3>{t("admin.runtime.configureHeading")}</h3>
          {!runtime ? <p className="launchpad__hint">{t("admin.runtime.notConfigured")}</p> : null}
          <form className="admin__form" onSubmit={(e) => void onSave(e)}>
            <p className="form__req-note">{t("common.requiredHint")}</p>
            <div className="admin__field">
              <label htmlFor="image_ref">
                {t("admin.runtime.field.imageRef")}
                <span className="field-req" aria-hidden="true">
                  *
                </span>
              </label>
              <input
                id="image_ref"
                required
                value={form.image_ref}
                onChange={(e) => setForm({ ...form, image_ref: e.target.value })}
              />
            </div>
            <div className="admin__field">
              <span className="field-head">
                <label htmlFor="internal_port">{t("admin.runtime.field.internalPort")}</label>
                <InfoTooltip text={t("admin.runtime.field.internalPortHint")} />
              </span>
              <input
                id="internal_port"
                type="number"
                min={1}
                max={65535}
                placeholder={t("admin.runtime.field.internalPortPlaceholder")}
                value={form.internal_port}
                onChange={(e) => setForm({ ...form, internal_port: e.target.value })}
              />
            </div>
            <div className="admin__field">
              <label htmlFor="env_text">{t("admin.runtime.field.env")}</label>
              <textarea
                id="env_text"
                rows={4}
                value={form.env_text}
                placeholder="PORT=8080&#10;DB_URL=postgres://…"
                onChange={(e) => setForm({ ...form, env_text: e.target.value })}
              />
            </div>
            <div className="admin__field">
              <span className="field-head">
                <label htmlFor="env_secrets_text">{t("admin.runtime.field.secretValues")}</label>
                <InfoTooltip text={t("admin.runtime.field.secretValuesHint")} />
              </span>
              {form.existing_secret_keys.length > 0 ? (
                <ul className="admin__secret-list">
                  {form.existing_secret_keys.map((key) => {
                    const pendingDelete = form.secret_deletes.includes(key);
                    return (
                      <li
                        key={key}
                        className={pendingDelete ? "admin__secret-item--delete" : undefined}
                      >
                        <code>{key}</code> <span aria-hidden="true">{"●●●●●"}</span>{" "}
                        <button
                          type="button"
                          onClick={() =>
                            setForm((f) => ({
                              ...f,
                              secret_deletes: pendingDelete
                                ? f.secret_deletes.filter((k) => k !== key)
                                : [...f.secret_deletes, key],
                            }))
                          }
                        >
                          {pendingDelete
                            ? t("admin.runtime.secretValueUndoDelete")
                            : t("admin.runtime.secretValueDelete")}
                        </button>
                        {pendingDelete ? (
                          <small>{t("admin.runtime.secretValuePendingDelete")}</small>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              ) : null}
              <textarea
                id="env_secrets_text"
                rows={3}
                value={form.env_secrets_text}
                placeholder="API_KEY=...&#10;DB_PASSWORD=..."
                onChange={(e) => setForm({ ...form, env_secrets_text: e.target.value })}
              />
            </div>
            <div className="admin__field">
              <label htmlFor="volumes_text">{t("admin.runtime.field.volumes")}</label>
              <textarea
                id="volumes_text"
                rows={3}
                value={form.volumes_text}
                placeholder={`appdata-${app.slug}-data=/var/lib/app`}
                onChange={(e) => setForm({ ...form, volumes_text: e.target.value })}
              />
            </div>
            {busy === "save" ? (
              <output className="admin__busy" aria-live="polite">
                <span className="spinner" aria-hidden="true" />
                {t("admin.runtime.busy.save")}
              </output>
            ) : null}
            <div className="admin__form-actions">
              <button type="submit" className="primary" disabled={busy !== null}>
                {t("admin.save")}
              </button>
              <button
                type="button"
                disabled={busy !== null}
                onClick={() => setForm(runtime ? runtimeToForm(runtime) : EMPTY_FORM)}
              >
                {t("admin.cancel")}
              </button>
              <button
                type="button"
                disabled={!runtime || busy !== null}
                onClick={() => void onFetchEnvTemplate()}
              >
                {busy === "envTemplate"
                  ? t("admin.runtime.fetchEnvTemplateRunning")
                  : t("admin.runtime.fetchEnvTemplateButton")}
              </button>
            </div>
          </form>

          {envTemplate ? (
            <EnvTemplatePanel
              template={envTemplate}
              configured={configuredKeys(form)}
              onPrefill={prefillEntry}
              onPrefillAll={prefillAllMissing}
            />
          ) : null}

          <h3>{t("admin.runtime.metadataHeading")}</h3>
          <MetadataDisplay metadata={metadata} />
        </>
      )}

      {deleting ? (
        <DeleteAppDialog
          appName={app.name}
          isManaged={isManaged}
          onCancel={() => setDeleting(false)}
          onConfirm={onDelete}
        />
      ) : null}
    </section>
  );
}

function MetadataDisplay(props: { metadata: AppMetadataFull | null }): JSX.Element {
  const { t } = useTranslation();
  const m = props.metadata;
  if (m === null) {
    return <p className="launchpad__hint">{t("admin.runtime.metadataEmpty")}</p>;
  }
  const rows: Array<[string, JSX.Element | string]> = [];
  if (m.vendor) rows.push([t("admin.runtime.meta.vendor"), m.vendor]);
  if (m.version) rows.push([t("admin.runtime.meta.version"), m.version]);
  if (m.category) rows.push([t("admin.runtime.meta.category"), m.category]);
  if (m.documentation_url)
    rows.push([t("admin.runtime.meta.documentation"), metaLink(m.documentation_url)]);
  if (m.homepage_url) rows.push([t("admin.runtime.meta.homepage"), metaLink(m.homepage_url)]);
  if (m.source_url) rows.push([t("admin.runtime.meta.source"), metaLink(m.source_url)]);
  if (m.capabilities.length > 0)
    rows.push([t("admin.runtime.meta.capabilities"), m.capabilities.join(", ")]);
  if (m.licenses.length > 0) rows.push([t("admin.runtime.meta.licenses"), m.licenses.join(", ")]);
  if (rows.length === 0) {
    return <p className="launchpad__hint">{t("admin.runtime.metadataEmpty")}</p>;
  }
  return (
    <>
      <p className="launchpad__hint">{t("admin.runtime.metadataHint")}</p>
      <table className="admin__table">
        <tbody>
          {rows.map(([label, value]) => (
            <tr key={label}>
              <th>{label}</th>
              <td>{value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

function metaLink(url: string): JSX.Element {
  return (
    <a href={url} target="_blank" rel="noopener noreferrer">
      {url}
    </a>
  );
}

function EnvTemplatePanel(props: {
  template: EnvTemplateResult | null;
  configured: ReadonlySet<string>;
  onPrefill: (entry: EnvTemplateEntry) => void;
  onPrefillAll: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  if (props.template === null) {
    return <p className="launchpad__hint">{t("admin.runtime.envTemplateIdle")}</p>;
  }
  if (props.template.entries.length === 0) {
    return <p className="launchpad__hint">{t("admin.runtime.envTemplateEmpty")}</p>;
  }
  return (
    <>
      <div className="admin__actions">
        <button type="button" onClick={props.onPrefillAll}>
          {t("admin.runtime.prefillAll")}
        </button>
        <small>
          {t("admin.runtime.envTemplateSource", { path: props.template.source_path ?? "—" })}
        </small>
      </div>
      <table className="admin__table">
        <thead>
          <tr>
            <th>{t("admin.runtime.col.envKey")}</th>
            <th>{t("admin.runtime.col.envComment")}</th>
            <th>{t("admin.runtime.col.envDefault")}</th>
            <th>{t("admin.col.actions")}</th>
          </tr>
        </thead>
        <tbody>
          {props.template.entries.map((entry) => {
            const isSet = props.configured.has(entry.key);
            return (
              <tr key={entry.key}>
                <td>
                  <code>{entry.key}</code>
                  {entry.is_secret_hint ? (
                    <em className="badge">{t("admin.runtime.secretBadge")}</em>
                  ) : null}
                </td>
                <td>{entry.comment ? <small>{entry.comment}</small> : "—"}</td>
                <td>
                  {entry.is_secret_hint ? (
                    "—"
                  ) : entry.default_value ? (
                    <code>{entry.default_value}</code>
                  ) : (
                    "—"
                  )}
                </td>
                <td>
                  {isSet ? (
                    <small className="env-template__set">{t("admin.runtime.envAlreadySet")}</small>
                  ) : (
                    <button type="button" onClick={() => props.onPrefill(entry)}>
                      {t("admin.runtime.prefill")}
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
}

function DeploymentHistory(props: { deployments: AppDeployment[] }): JSX.Element {
  const { t } = useTranslation();
  if (props.deployments.length === 0) {
    return <p className="launchpad__hint">{t("admin.runtime.noDeployments")}</p>;
  }
  return (
    <table className="admin__table">
      <thead>
        <tr>
          <th>{t("admin.runtime.col.startedAt")}</th>
          <th>{t("admin.runtime.col.image")}</th>
          <th>{t("admin.runtime.col.result")}</th>
          <th>{t("admin.runtime.col.triggeredBy")}</th>
        </tr>
      </thead>
      <tbody>
        {props.deployments.map((d) => (
          <tr key={d.id}>
            <td>
              <small>{d.started_at}</small>
            </td>
            <td>
              <code>{d.image_ref}</code>
              {d.image_digest ? (
                <>
                  <br />
                  <small>{shortDigest(d.image_digest)}</small>
                </>
              ) : null}
            </td>
            <td>
              <span
                className={
                  d.status === "healthy"
                    ? "admin__probe admin__probe--ok"
                    : d.status === "failed"
                      ? "admin__probe admin__probe--err"
                      : "admin__probe"
                }
              >
                {d.status}
              </span>
              {d.error ? (
                <>
                  <br />
                  <small className="launchpad__hint--error">{d.error}</small>
                </>
              ) : null}
            </td>
            <td title={d.triggered_by}>{d.triggered_by_display ?? shortenId(d.triggered_by)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
